<template>
  <div class="scroll">
    <div class="ui container">
      <div
        v-if="!this.embedded"
        class="ui header"
      >
        Add Alert
      </div>
      <div class="ui segments">
        <header-segment>
          What should OWAL notify you about?
        </header-segment>
        <div class="ui eight horizontal segments">
          <alert-settings-option-card
            v-for="option in labels"
            :key="option.identifier"
            :data="option"
            :selected="option.identifier === selectedCategoryIdentifier"
            @click.native="selectCategory(option.identifier)"
          />
        </div>
      </div>

      <div
        :class="this.embedded? '' : 'ui segments'"
      >
        <header-segment>
          When should OWAL notify you?
        </header-segment>
        <div
          :class="this.embedded ? '' : 'ui segment'"
          :style="this.embedded ? 'border: none; padding: 15px;' : ''"
        >
          <form class="ui form">
            <div class="ui inline field">
              <label>
                Every
              </label>
              <picker
                class="picker selection"
                :items="dayOptions"
                placeholder="Day"
                display-property="name"
                key-property="value"
              />
              <picker
                class="picker selection"
                :items="timeRangeOptions"
                placeholder="Between"
                display-property="name"
                key-property="value"
                @change="selectRangeType"
              />
              <dynamic-date-picker v-if="this.rangeType === 'between'" />
              <label v-if="this.rangeType === 'between'">
                and
              </label>
              <dynamic-date-picker v-if="this.rangeType === 'between'" />
            </div>
          </form>
        </div>
        <!-- Idle Times -->
        <div
          :class="this.embedded ? '' : 'ui segment'"
          :style="this.embedded ? 'border: none; padding: 15px;' : ''"
        >
          <form class="ui form">
            <div class="ui inline field">
              <label>Alert if someone is idle for </label>
              <picker
                class="ui compact picker selection"
                :items="idleTimes"
                placeholder="5"
                display-property="name"
                key-property="value"
                @change="selectIdleTime"
              /> seconds.
            </div>
          </form>
        </div>

        <!-- Rate Limits -->
        <div class="ui segment">
          <form class="ui form">
            <div class="ui inline field">
              <label>Send one alert per </label>
              <picker
                class="ui compact picker selection"
                :items="rateLimitTimes"
                placeholder="5"
                display-property="name"
                key-property="value"
                @change="selectInterval"
              /> seconds.
            </div>
          </form>
        </div>
      </div>

      <div class="ui segments">
        <header-segment>
          Which cameras should OWAL notify you about?
        </header-segment>
        <div class="ui segment">
          <div class="ui form">
            <owal-form-field
              :field="this.camerasField"
            />
          </div>
        </div>
        <div class="" />
      </div>
    </div>
  </div>
</template>

<script>
import AlertSettingsOptionCard from '@/components/alerts/AlertSettingsOptionCard'
import HeaderSegment from '@/components/shared/HeaderSegment'
import * as forms from '@/libraries/forms/forms'
import OwalFormField from '@/libraries/owal-ui/forms/OwalFormField'
import DynamicDatePicker from '@/components/shared/DynamicDatePicker'
import Picker from '@/components/shared/Picker'
import { inputPanel } from '@/libraries/owal-ui/mixins/inputPanel'

import { mapGetters } from 'vuex'

export default {
  name: 'AlertSettings',
  components: { AlertSettingsOptionCard, HeaderSegment, OwalFormField, DynamicDatePicker, Picker },
  mixins: [inputPanel],
  data () {
    return {
      selectedCategoryIdentifier: '',
      rangeType: 'between',
      idleTime: 5,
      interval: 5,
      camerasField: new forms.FormField({
        type: forms.FormFieldType.CHECKBOX,
        label: 'Cameras',
        identifier: 'cameras',
        options: this.camerasForCurrentDomain.map(camera => new forms.FormFieldSelectOption(camera.description, camera.uuid)),
        defaultValue: [],
        multiselect: true,
        showSelectAll: true,
      }),
    }
  },
  created: function () {
    this.observeCameraChange()
  },
  computed: {
    ...mapGetters(['camerasForCurrentDomain']),
    labels: function () {
      return [
        {
          label: 'Faces',
          identifier: 'alert.category.faces',
          icons: [
            'smile',
          ],
          // image: 'faces'
        },
        {
          label: 'Contraband',
          identifier: 'alert.category.smoking',
          icons: ['spy', 'spoon'],
        },
        {
          label: 'Bike',
          identifier: 'alert.category.bike',
          icons: ['bicycle', 'tree'],
        },
        {
          label: 'Vehicle',
          identifier: 'alert.category.vehicle',
          icons: ['car'],
        },
        {
          label: 'Crowd',
          identifier: 'alert.category.crowd',
          icons: ['users'],
        },
        {
          label: 'Pet',
          identifier: 'alert.category.pet',
          icons: ['paw'],
        },
        {
          label: 'Delivery',
          identifier: 'alert.category.package',
          icons: ['building', 'truck'],
        },
        {
          label: 'Other',
          identifier: 'alert.category.other',
          icons: ['group object'],
        },
      ]
    },
    dayOptions: function () {
      const days = [
        'Day',
        'Weekday',
        'Weekend',
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
      ]
      const options = days.map(day => new forms.FormFieldSelectOption(day, day.toLowerCase()))

      return options
    },
    timeRangeOptions: function () {
      const timeRangeOptions = [
        'All Day',
        'Between',
      ]
      const options = timeRangeOptions.map(option => new forms.FormFieldSelectOption(option, option.toLowerCase()))
      return options
    },
    idleTimes: function () {
      const timeRangeOptions = [5, 10, 15, 20, 30, 45, 60, 90]
      const options = timeRangeOptions.map(option => new forms.FormFieldSelectOption(option.toString(10), option))
      return options
    },
    rateLimitTimes: function () {
      return this.idleTimes
    },
  },
  methods: {
    observeCameraChange () {
      this.$store.watch((value, oldValue) => {
        this.updateCamerasField()
      })
    },
    updateCamerasField () {
      this.camerasField = new forms.FormField({
        type: forms.FormFieldType.CHECKBOX,
        label: 'Cameras',
        identifier: 'cameras',
        options: this.camerasForCurrentDomain.map(camera => new forms.FormFieldSelectOption(camera.description, camera.uuid)),
        defaultValue: [],
        multiselect: true,
        showSelectAll: true,
      })
    },
    selectIdleTime: function (option) {
      this.idleTime = option.value
    },
    selectInterval: function (option) {
      this.interval = option.value
    },
    selectCategory: function (identifier) {
      this.selectedCategoryIdentifier = identifier
    },
    selectRangeType: function (type) {
      this.rangeType = type.value
    },
  },
}
</script>

<style scoped>

.scroll{
  padding-top: 40px;
  padding-bottom: 40px;
  height: 100%;
  overflow-y: auto;
}
</style>
